import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { useToast } from '@astryxdesign/core/Toast';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { Notice, PillButton, Skeleton } from '@/components/kit';
import { ClientHeader } from '@/features/studio/ClientHeader';
import { ErrorState } from '@/components/QueryState';
import { publicApi } from '@/lib/api/public';
import { errorMessage } from '@/lib/api/errors';
import type { PublicBooking } from '@/lib/api/schemas';
import { forgetDeviceBooking, importTokenFromHash, listDeviceBookings } from '@/lib/device-bookings';
import { formatDateTime } from '@/lib/format';
import { useDocumentTitle } from '@/lib/hooks';
import { useStudio } from '@/features/studio/studio-data';
import { useBookingNav } from '@/features/booking/useBookingNav';
import { ReminderActions } from '@/features/booking/ReminderActions';
import { BookingStatusCard, STATUS_TEXT } from '@/features/booking/BookingStatusCard';

export function MyBookingsPage() {
  const studio = useStudio();
  const booking = useBookingNav();
  useDocumentTitle(`Моя запись — ${studio.name}`);
  const [imported] = useState(() => importTokenFromHash(studio.slug));
  const [version, setVersion] = useState(0);
  const tokens = useMemo(() => listDeviceBookings(studio.slug).map((b) => b.token), [studio.slug, imported, version]);
  const refreshTokens = () => setVersion((v) => v + 1);

  const query = useQuery({
    queryKey: ['my-bookings', studio.slug, tokens.join(',')],
    queryFn: () => publicApi.bookings(studio.slug, tokens),
    staleTime: 15_000,
  });

  const now = Date.now();
  const sorted = useMemo(() => {
    const list = query.data ?? [];
    const upcoming = list.filter(
      (b) => ['confirmed', 'arrived'].includes(b.status) && new Date(b.endsAt).getTime() > now - 3_600_000,
    );
    const past = list.filter((b) => !upcoming.includes(b));
    upcoming.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    past.sort((a, b) => b.startsAt.localeCompare(a.startsAt));
    return { upcoming, past };
  }, [query.data, now]);

  const empty = (title: string, text: string) => (
    <div className="ui-card" style={{ textAlign: 'center', padding: 'var(--spacing-6) var(--spacing-5)' }}>
      <div className="ui-stack" style={{ alignItems: 'center' }}>
        <span className="ui-badge-icon ui-badge-icon--soft">
          <CalendarBlankIcon size={22} aria-hidden />
        </span>
        <h2 className="ui-h3">{title}</h2>
        <p className="ui-muted">{text}</p>
        <PillButton variant="primary" block onClick={() => booking.open()}>
          Записаться
        </PillButton>
      </div>
    </div>
  );

  return (
    <div className="ui-page">
      <ClientHeader />
      <h1 className="ui-h1">Моя запись</h1>

      {tokens.length === 0 ? (
        empty(
          'На этом устройстве записей нет',
          'Запишитесь — запись появится здесь. С другого телефона её можно открыть по ссылке из записи.',
        )
      ) : query.isPending ? (
        <div className="ui-stack">
          <Skeleton height={170} label="Загружаем ваши записи" />
          <Skeleton height={120} />
        </div>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} title="Не удалось загрузить записи" />
      ) : (query.data ?? []).length === 0 ? (
        empty('Записи не найдены', 'Возможно, студия удалила демонстрационные записи. Запишитесь снова.')
      ) : (
        <>
          {sorted.upcoming.length === 0
            ? empty('Предстоящих записей нет', 'Выберите услугу и удобное время — это займёт меньше минуты.')
            : sorted.upcoming.map((b) => <UpcomingBooking key={b.id} booking={b} />)}
          {sorted.past.length > 0 ? (
            <section className="ui-stack" aria-label="Прошедшие и отменённые">
              <h2 className="ui-h2">Прошедшие и отменённые</h2>
              <div className="ui-list">
                {sorted.past.map((b) => (
                  <div key={b.id} className="ui-list__item" style={{ cursor: 'default' }} data-testid="my-booking">
                    <span className="ui-stack ui-grow" style={{ ['--ui-gap' as string]: '3px' }}>
                      <span className="ui-title">{b.service.name}</span>
                      <span className="ui-caption">
                        {formatDateTime(b.startsAt, b.studio.timezone)} · {STATUS_TEXT[b.status]}
                      </span>
                    </span>
                    <PillButton
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        forgetDeviceBooking(studio.slug, b.token ?? '');
                        refreshTokens();
                      }}
                    >
                      Убрать
                    </PillButton>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function UpcomingBooking({ booking }: { booking: PublicBooking }) {
  const studio = useStudio();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const tz = booking.studio.timezone;
  const token = booking.token ?? '';

  const cancel = useMutation({
    mutationFn: () => publicApi.cancel(studio.slug, token),
    onSuccess: () => {
      setConfirming(false);
      toast({ body: 'Запись отменена' });
      void queryClient.invalidateQueries({ queryKey: ['my-bookings', studio.slug] });
      void queryClient.invalidateQueries({ queryKey: ['availability', studio.slug] });
    },
  });

  return (
    <section
      className="ui-stack"
      style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}
      data-testid="my-booking"
      aria-label={booking.service.name}
    >
      <BookingStatusCard booking={booking} />

      <ReminderActions booking={booking} token={token} />

      {booking.status === 'confirmed' ? (
        booking.canCancel ? (
          <div className="ui-stack" style={{ ['--ui-gap' as string]: '2px', alignItems: 'center' }}>
            <button type="button" className="ui-danger-link" onClick={() => setConfirming(true)}>
              Отменить запись
            </button>
            <span className="ui-caption" style={{ textAlign: 'center' }}>
              Онлайн — до {formatDateTime(booking.cancelDeadline, tz)}, позже — по телефону студии
            </span>
          </div>
        ) : (
          <p className="ui-caption" style={{ textAlign: 'center' }}>
            Онлайн-отмена была доступна до {formatDateTime(booking.cancelDeadline, tz)}. Сейчас — только по телефону студии.
          </p>
        )
      ) : null}
      {cancel.isError ? (
        <Notice tone="error" title="Не удалось отменить">
          {errorMessage(cancel.error)}
        </Notice>
      ) : null}

      <AlertDialog
        isOpen={confirming}
        onOpenChange={setConfirming}
        title="Отменить запись?"
        description={`${booking.service.name}, ${formatDateTime(booking.startsAt, tz)}. Время освободится для других клиентов.`}
        actionLabel="Отменить запись"
        cancelLabel="Оставить"
        onAction={() => cancel.mutate()}
        isActionLoading={cancel.isPending}
      />
    </section>
  );
}
